import React from 'react';
import type { IProps as ISingleColumnLayoutProps } from './SingleColumnLayout.interface';
import { SingleColumnLayout } from './SingleColumnLayout.component';
import TranslationComponent from '@/utils/TranslationComponent/TranslationComponent'; // Corrected import
import type { BlockEntry } from '@/types/blocks'; // Assuming BlockEntry is the expected item structure

/**
 * @interface ISingleColumnLayoutInputProps
 * Defines the expected input props for the SingleColumnLayout from an external source (e.g., CMS).
 * This interface should mirror the structure of data as it comes from the source.
 * It includes `items` for the blocks and allows passthrough of other props for BlockWrapper.
 *
 * @property {BlockEntry[]} items - An array of block entries.
 * @property {any} [key: string] - Allows passthrough of other props (e.g., for BlockWrapper).
 */
export interface ISingleColumnLayoutInputProps {
    items: BlockEntry[];
    [key: string]: any; // For BlockWrapper props and other potential passthroughs
}

/**
 * Maps input props from an external source to the props expected by the SingleColumnLayout component.
 *
 * @param {ISingleColumnLayoutInputProps} input - The input props from the external source.
 * @returns {ISingleColumnLayoutProps} The mapped props for the SingleColumnLayout component.
 */
const mapInputToProps = (input: ISingleColumnLayoutInputProps): ISingleColumnLayoutProps => {
    const { items, ...blockWrapperProps } = input;

    // Potentially transform items if their structure from CMS differs from BlockEntry
    // For now, assuming a direct match for `items`.

    return {
        items,
        ...blockWrapperProps, // Pass through BlockWrapper related settings
    } as ISingleColumnLayoutProps; // Type assertion might be needed if structure isn't perfectly aligned
};

/**
 * Translation component for SingleColumnLayout.
 * It adapts props from an external data source to the SingleColumnLayout component's expected props.
 */
const SingleColumnLayoutTranslation: React.FC<ISingleColumnLayoutInputProps> = (inputProps) => {
    return (
        <TranslationComponent
            inputProps={inputProps}
            TargetComponent={SingleColumnLayout}
            mapProps={mapInputToProps}
        />
    );
};

export default SingleColumnLayoutTranslation;
